import { CREATE_COMMENT_REQUEST, CREATE_COMMENT_SUCCESS, CREATE_COMMENT_FAILURE, DELETE_COMMENT_SUCCESS, DELETE_COMMENT_REQUEST, DELETE_COMMENT_FAILURE, UPDATE_COMMENT_REQUEST, UPDATE_COMMENT_FAILURE, UPDATE_COMMENT_SUCCESS, CANCEL_CREATING_COMMENT, EDIT_COMMENT, RETRY_CREATE_COMMENT_REQUEST, CANCEL_EDITING_COMMENT, EDIT_COMMENT_CREATE_ERROR, } from './top-level-comments-crud-actions';
import { assertDefined, isOfType, unreachable } from '../../../../ts-utils';
import { omit } from 'lodash';
import { getChildrenIds } from '../replies/replies-selectors';
import { getVotesStateIN } from '../votes/comments-vote-utils';
import { hiddenComponentEffectsGuard } from '../effects/hidden-component-effects-guard';
import { topLevelCommentsRatingsReducer } from './top-level-comments-ratings-reducer';
export function commentsCrudReducer(state, action) {
    switch (action.type) {
        case CANCEL_CREATING_COMMENT: {
            const { localId } = action.payload;
            const currentState = state.commentsById[localId];
            if (currentState.type !== 'COMMENT_CREATE_ERROR_EDITING') {
                return state;
            }
            return {
                ...state,
                commentsById: omit(state.commentsById, localId),
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    chunks: state.topLevelCommentList.chunks.filter((c) => c.type !== 'NEW' || c.localOrCommentId !== localId),
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case CANCEL_EDITING_COMMENT: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType('COMMENT_EDITING', currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: {
                        // we do not spread currentState to not pass content field
                        type: 'COMMENT_READY',
                        comment: currentState.comment,
                        commentId: currentState.commentId,
                        reactions: currentState.reactions,
                        marked: currentState.marked,
                        votes: currentState.votes,
                        ...(currentState.localId ? { localId: currentState.localId } : {}),
                    },
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case EDIT_COMMENT: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType(['COMMENT_READY', 'COMMENT_UPDATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: { ...currentState, type: 'COMMENT_EDITING' },
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case CREATE_COMMENT_REQUEST: {
            const { localId, content, position, asGuest, authorName } = action.payload;
            return {
                ...state,
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    chunks: position === 'start'
                        ? [{ type: 'NEW', localOrCommentId: localId }, ...state.topLevelCommentList.chunks]
                        : [...state.topLevelCommentList.chunks, { type: 'NEW', localOrCommentId: localId }],
                },
                commentsById: {
                    ...state.commentsById,
                    [localId]: {
                        type: 'COMMENT_CREATE_PENDING',
                        localId,
                        content,
                        asGuest,
                        authorName,
                    },
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case RETRY_CREATE_COMMENT_REQUEST: {
            const { localId, content, asGuest, authorName } = action.payload;
            const currentState = state.commentsById[localId];
            if (!isOfType(['COMMENT_CREATE_ERROR', 'COMMENT_CREATE_ERROR_EDITING'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [localId]: {
                        type: 'COMMENT_CREATE_PENDING',
                        content,
                        localId,
                        asGuest,
                        authorName,
                    },
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case CREATE_COMMENT_SUCCESS: {
            const { comment, localId, vote } = action.payload;
            assertDefined(comment);
            const currentState = state.commentsById[localId];
            if (currentState.type !== 'COMMENT_CREATE_PENDING') {
                return state;
            }
            const commentState = {
                type: 'COMMENT_READY',
                localId: currentState.localId,
                comment,
                commentId: comment.commentId,
                reactions: { total: 0, reactions: [], type: 'IDLE' },
                marked: { type: 'IDLE' },
                votes: getVotesStateIN(comment, vote ? [vote] : []),
                isModerationPending: comment.status === 'PENDING',
            };
            return {
                ...state,
                commentsById: {
                    ...omit(state.commentsById, localId),
                    [comment.commentId]: commentState,
                },
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    chunks: state.topLevelCommentList.chunks.map((chunk) => chunk.type === 'NEW' && chunk.localOrCommentId === localId
                        ? { type: 'NEW', localOrCommentId: comment.commentId }
                        : chunk),
                },
                replyLists: {
                    ...state.replyLists,
                    [comment.commentId]: {
                        parentCommentId: comment.commentId,
                        newRepliesToReplies: {},
                        nextChunkState: { type: 'IDLE' },
                        prevChunkState: { type: 'IDLE' },
                        liveRepliesPurgatory: [],
                        chunks: [],
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_SUCCESS', id: comment.commentId, timestamp: Date.now() },
                    toast: {
                        isHidden: true,
                        skin: 'success',
                        message: { type: 'TRANSLATION', key: 'toast.comment-published' },
                        timestamp: Date.now(),
                    },
                    modal: comment.status === 'PENDING'
                        ? { type: 'pending-comment', timestamp: Date.now() }
                        : state.effects?.modal,
                }),
                ratings: topLevelCommentsRatingsReducer(state, action),
                totals: state.totals + 1,
            };
        }
        case CREATE_COMMENT_FAILURE: {
            const { localId, message, rating, authorName, email } = action.payload;
            const currentState = state.commentsById[localId];
            if (currentState.type !== 'COMMENT_CREATE_PENDING') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [localId]: {
                        ...currentState,
                        type: 'COMMENT_CREATE_ERROR',
                        pendingRatingAction: rating,
                        errorMessage: message,
                        authorName,
                        email,
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_ERROR', id: localId, timestamp: Date.now() },
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                    modal: message.type === 'MODAL'
                        ? { type: message.modalType, timestamp: Date.now() }
                        : state.effects?.modal,
                }),
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case EDIT_COMMENT_CREATE_ERROR: {
            const { localId, authorName, email } = action.payload;
            const currentState = state.commentsById[localId];
            if (currentState.type !== 'COMMENT_CREATE_ERROR') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [localId]: {
                        ...currentState,
                        type: 'COMMENT_CREATE_ERROR_EDITING',
                        authorName,
                        email,
                    },
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case UPDATE_COMMENT_REQUEST: {
            const { commentId, content } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType(['COMMENT_EDITING', 'COMMENT_UPDATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: {
                        type: 'COMMENT_UPDATE_PENDING',
                        localId: currentState.localId,
                        commentId: currentState.commentId,
                        comment: currentState.comment,
                        votes: currentState.votes,
                        content,
                        reactions: currentState.reactions,
                        marked: currentState.marked,
                    },
                },
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case UPDATE_COMMENT_FAILURE: {
            const { message, commentId, rating } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'COMMENT_UPDATE_PENDING') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: {
                        ...currentState,
                        type: 'COMMENT_UPDATE_ERROR',
                        pendingRatingAction: rating,
                        errorMessage: message,
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_ERROR', id: commentId, timestamp: Date.now() },
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                    modal: message.type === 'MODAL'
                        ? { type: message.modalType, timestamp: Date.now() }
                        : state.effects?.modal,
                }),
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case UPDATE_COMMENT_SUCCESS: {
            const { comment, commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'COMMENT_UPDATE_PENDING') {
                return state;
            }
            assertDefined(comment);
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [comment.commentId]: {
                        ...currentState,
                        type: 'COMMENT_READY',
                        comment,
                        isModerationPending: !!comment.draftContent || comment.status === 'PENDING',
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_SUCCESS', id: comment.commentId, timestamp: Date.now() },
                    modal: comment.status === 'PENDING' || comment.draftContent
                        ? { type: 'pending-comment', timestamp: Date.now() }
                        : state.effects?.modal,
                }),
                ratings: topLevelCommentsRatingsReducer(state, action),
            };
        }
        case DELETE_COMMENT_REQUEST: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType(['COMMENT_READY', 'COMMENT_UPDATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: { ...currentState, type: 'COMMENT_DELETE_PENDING' },
                },
            };
        }
        case DELETE_COMMENT_FAILURE: {
            const { commentId, message } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'COMMENT_DELETE_PENDING') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: { ...currentState, type: 'COMMENT_READY' },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_ERROR', id: commentId, timestamp: Date.now() },
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        case DELETE_COMMENT_SUCCESS: {
            const { commentId } = action.payload;
            const commentRepliesIds = getChildrenIds(state.commentsById)(commentId);
            const updatedReplies = commentRepliesIds.reduce((acc, replyId) => {
                const replyState = state.commentsById[replyId];
                if ('targetStatus' in replyState) {
                    acc[replyId] = { ...replyState, targetStatus: 'DELETED' };
                }
                return acc;
            }, {});
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    ...updatedReplies,
                    [commentId]: { type: 'COMMENT_DELETED', commentId },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_SUCCESS', id: commentId, timestamp: Date.now() },
                }),
                ratings: topLevelCommentsRatingsReducer(state, action),
                totals: state.totals - 1,
            };
        }
        default:
            throw unreachable(action);
    }
}
//# sourceMappingURL=top-level-comments-crud-reducer.js.map